import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Wallet,
  Inbox,
  CheckCircle2,
  AlertCircle,
  Check,
  X,
  Building2,
  Users,
  Info,
  Calendar,
  IndianRupee,
  Clock,
  ArrowRight,
  ShieldCheck,
  RotateCcw
} from 'lucide-react';
import { formatMoney, isAdmin } from '@vns/core';
import { useSession } from '../lib/session';
import {
  useNotifications,
  useDismissNotification,
  useMarkRead,
  useMarkAllRead,
  useDismissAll,
  KIND_META,
  type NotificationKind,
  type NotificationRow,
} from '../lib/notifications';
import Shell from '../components/Shell';
import {
  EmptyState,
  TableSkeleton,
  ErrorState,
  useToast,
} from '../components/ui';
import { type LucideIcon } from 'lucide-react';

const ORDER: NotificationKind[] = [
  'payment_due',
  'approval_pending',
  'entry_rejected',
  'entry_approved',
];

const KIND_CONFIG: Record<
  NotificationKind,
  {
    icon: LucideIcon;
    accentColor: string;
    bgColor: string;
    borderColor: string;
  }
> = {
  payment_due: {
    icon: Wallet,
    accentColor: '#ea580c',
    bgColor: 'rgba(234, 88, 12, 0.08)',
    borderColor: 'rgba(234, 88, 12, 0.2)',
  },
  approval_pending: {
    icon: Inbox,
    accentColor: '#2563eb',
    bgColor: 'rgba(37, 99, 235, 0.08)',
    borderColor: 'rgba(37, 99, 235, 0.2)',
  },
  entry_approved: {
    icon: CheckCircle2,
    accentColor: '#16a34a',
    bgColor: 'rgba(22, 163, 74, 0.08)',
    borderColor: 'rgba(22, 163, 74, 0.2)',
  },
  entry_rejected: {
    icon: AlertCircle,
    accentColor: '#dc2626',
    bgColor: 'rgba(239, 68, 68, 0.08)',
    borderColor: 'rgba(239, 68, 68, 0.2)',
  },
};

type FilterTab = 'all' | 'unread' | 'payment_due' | 'approvals';

export default function Notifications() {
  const navigate = useNavigate();
  const actor = useSession((s) => s.actor);
  const admin = isAdmin(actor);
  const [filter, setFilter] = useState<FilterTab>('all');
  const { data, isLoading, error, refetch } = useNotifications();
  const markOneRead = useMarkRead();
  const dismissOne = useDismissNotification();
  const markRead = useMarkAllRead();
  const dismissAll = useDismissAll();
  const toast = useToast();

  const allRows = data ?? [];
  const rows = admin ? allRows : allRows.filter((n) => n.kind !== 'payment_due');
  const unreadRows = rows.filter((n) => !n.readAt);
  const unreadIds = unreadRows.map((n) => n.id);
  const paymentDueRows = rows.filter((n) => n.kind === 'payment_due');
  const approvalRows = rows.filter((n) => n.kind !== 'payment_due');

  const filteredRows = rows.filter((n) => {
    if (filter === 'unread') return !n.readAt;
    if (filter === 'payment_due') return n.kind === 'payment_due';
    if (filter === 'approvals') return n.kind !== 'payment_due';
    return true;
  });

  const groups = ORDER
    .map((kind) => ({ kind, items: filteredRows.filter((n) => n.kind === kind) }))
    .filter((g) => g.items.length > 0);

  return (
    <Shell
      title="Notifications"
      crumb="Inbox"
      actions={
        rows.length > 0 && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {unreadIds.length > 0 && (
              <button
                className="btn btn-auto btn-sm"
                onClick={async () => {
                  await markRead.mutateAsync(unreadIds);
                  toast.show('All notifications marked as read');
                }}
                style={{ display: 'flex', alignItems: 'center', gap: 5, borderRadius: 'var(--radius-sm)' }}
              >
                <Check size={13} />
                <span>Mark all as read</span>
              </button>
            )}
            <button
              className="btn btn-auto btn-sm btn-ghost"
              onClick={async () => {
                await dismissAll.mutateAsync(rows.map((n) => n.id));
                toast.show('All notifications cleared');
              }}
              style={{ display: 'flex', alignItems: 'center', gap: 5, borderRadius: 'var(--radius-sm)' }}
            >
              <span>Clear all</span>
            </button>
          </div>
        )
      }
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        {/* Filter Tabs Bar */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            marginBottom: 20,
            flexWrap: 'wrap',
            alignItems: 'center',
            background: 'var(--surface)',
            padding: '10px 14px',
            borderRadius: 'var(--radius)',
            border: '1px solid var(--border)',
          }}
        >
          <button
            className={`btn btn-auto btn-sm ${filter === 'all' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setFilter('all')}
            style={{ borderRadius: 'var(--radius-sm)', fontSize: 13 }}
          >
            All Alerts ({rows.length})
          </button>
          <button
            className={`btn btn-auto btn-sm ${filter === 'unread' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setFilter('unread')}
            style={{ borderRadius: 'var(--radius-sm)', fontSize: 13, position: 'relative' }}
          >
            Unread ({unreadRows.length})
            {unreadRows.length > 0 && (
              <span
                style={{
                  display: 'inline-block',
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: '#ea580c',
                  marginLeft: 6,
                }}
              />
            )}
          </button>
          {admin && (
            <button
              className={`btn btn-auto btn-sm ${filter === 'payment_due' ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setFilter('payment_due')}
              style={{ borderRadius: 'var(--radius-sm)', fontSize: 13 }}
            >
              Pending Payments ({paymentDueRows.length})
            </button>
          )}
          {approvalRows.length > 0 && (
            <button
              className={`btn btn-auto btn-sm ${filter === 'approvals' ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setFilter('approvals')}
              style={{ borderRadius: 'var(--radius-sm)', fontSize: 13 }}
            >
              Site Approvals & Reviews ({approvalRows.length})
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="card" style={{ padding: 20 }}>
            <TableSkeleton rows={4} cols={3} />
          </div>
        ) : error ? (
          <div className="card" style={{ padding: 20 }}>
            <ErrorState error={error} onRetry={() => void refetch()} />
          </div>
        ) : !filteredRows.length ? (
          <div className="card" style={{ padding: '40px 20px' }}>
            <EmptyState
              icon="check"
              title={filter === 'unread' ? 'No unread notifications' : 'No notifications in this view'}
              hint={
                filter === 'unread'
                  ? 'All reminders, payment alerts, and site updates have been read.'
                  : 'Everything is up to date and in sync.'
              }
            />
          </div>
        ) : (
          groups.map((g) => {
            const config = KIND_CONFIG[g.kind];
            const IconComp = config.icon;

            return (
              <div
                key={g.kind}
                className="card"
                style={{
                  marginBottom: 18,
                  borderRadius: 'var(--radius)',
                  border: '1px solid var(--border)',
                  overflow: 'hidden',
                  padding: 0,
                }}
              >
                <div
                  className="card-head"
                  style={{
                    borderBottom: '1px solid var(--border)',
                    padding: '14px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    background: 'var(--surface-muted)',
                  }}
                >
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 'var(--radius-xs)',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: config.bgColor,
                      color: config.accentColor,
                      border: `1px solid ${config.borderColor}`,
                    }}
                  >
                    <IconComp size={16} />
                  </div>
                  <div>
                    <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0, color: 'var(--text)' }}>
                      {KIND_META[g.kind].heading}
                    </h3>
                    <div style={{ fontSize: 12.5, color: 'var(--text-soft)', marginTop: 2 }}>
                      {KIND_META[g.kind].blurb}
                    </div>
                  </div>
                  <span
                    style={{
                      marginLeft: 'auto',
                      background: config.bgColor,
                      color: config.accentColor,
                      fontWeight: 600,
                      fontSize: 11.5,
                      padding: '2px 8px',
                      borderRadius: 'var(--radius-xs)',
                      border: `1px solid ${config.borderColor}`,
                    }}
                  >
                    {g.items.length} {g.items.length === 1 ? 'item' : 'items'}
                  </span>
                </div>

                <div>
                  {g.items.map((n) => (
                    <NotificationCard
                      key={n.id}
                      n={n}
                      onOpen={async () => {
                        if (!n.readAt) {
                          await markOneRead.mutateAsync(n.id);
                        }
                        if (n.kind === 'approval_pending') navigate('/approvals');
                        else if (n.kind === 'entry_approved' || n.kind === 'entry_rejected')
                          navigate('/my-entries');
                        else if (n.projectId) navigate(`/projects/${n.projectId}`);
                      }}
                      onMarkRead={() => markOneRead.mutate(n.id)}
                      onDismiss={() => dismissOne.mutate(n.id)}
                    />
                  ))}
                </div>
              </div>
            );
          })
        )}

        {/* Informational Guide */}
        <div
          className="card"
          style={{
            marginTop: 24,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            padding: '18px 22px',
          }}
        >
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <Info size={16} style={{ color: 'var(--brand)' }} />
            <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>
              How Notifications & Payment Reminders Work
            </h4>
          </div>
          <ul
            style={{
              margin: 0,
              paddingLeft: 20,
              fontSize: 12.5,
              color: 'var(--text-soft)',
              lineHeight: 1.75,
            }}
          >
            <li>
              <strong>Pending Payment Reminders:</strong> Triggered automatically for projects with an active outstanding client balance.
            </li>
            <li>
              <strong>Unread Counter:</strong> The notification badge reflects unread alerts in real time.
            </li>
            <li>
              <strong>Automatic Clearance:</strong> Once payment is logged or entry is reviewed, relevant reminders automatically resolve.
            </li>
          </ul>
        </div>
      </motion.div>
    </Shell>
  );
}

function NotificationCard({
  n,
  onOpen,
  onMarkRead,
  onDismiss,
}: {
  n: NotificationRow;
  onOpen: () => void;
  onMarkRead: () => void;
  onDismiss: () => void;
}) {
  const when = new Date(n.createdAt).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  const isPaymentDue = n.kind === 'payment_due';
  const isUnread = !n.readAt;

  return (
    <div className={`notif-card ${isUnread ? 'is-unread' : ''}`}>
      <div className="notif-card-main" onClick={onOpen}>
        <div className="notif-title-row">
          {isUnread && (
            <span className="notif-unread-badge">
              Unread
            </span>
          )}
          <span className="notif-title-text">
            {n.title}
          </span>
          {n.projectName && (
            <span className="notif-project-badge">
              <Building2 size={11} /> {n.projectName}
            </span>
          )}
          {n.clientName && (
            <span className="notif-client-badge">
              <Users size={11} /> {n.clientName}
            </span>
          )}
        </div>

        {n.body && (
          <div className="notif-body">
            {n.body}
          </div>
        )}

        {/* Financial details breakdown */}
        {isPaymentDue && (
          <div className="notif-finance-box">
            {n.projectValuePaise != null && (
              <div className="notif-finance-item">
                <span className="notif-finance-lbl">Total Project Budget: </span>
                <strong style={{ color: 'var(--text)' }} className="num">{formatMoney(n.projectValuePaise)}</strong>
              </div>
            )}
            {n.paidAmountPaise != null && (
              <div className="notif-finance-item">
                <span className="notif-finance-lbl">Received: </span>
                <strong style={{ color: '#16a34a' }} className="num">{formatMoney(n.paidAmountPaise)}</strong>
              </div>
            )}
            {n.amountPaise != null && (
              <div className="notif-finance-item">
                <span className="notif-finance-lbl">Pending Balance: </span>
                <strong style={{ color: '#ea580c' }} className="num">{formatMoney(n.amountPaise)}</strong>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="notif-actions-col">
        <div className="notif-date">{when}</div>
        <div className="notif-actions-btns">
          {isUnread && (
            <button
              className="btn btn-auto btn-sm btn-ghost notif-action-btn"
              onClick={(e) => {
                e.stopPropagation();
                onMarkRead();
              }}
              title="Mark as read"
            >
              <Check size={12} />
              <span>Mark Read</span>
            </button>
          )}
          <button
            className="btn btn-auto btn-sm btn-ghost notif-action-btn"
            onClick={onOpen}
          >
            <span>{isPaymentDue ? 'Open Project' : 'View'}</span>
          </button>
          <button
            className="icon-btn notif-dismiss-btn"
            onClick={(e) => {
              e.stopPropagation();
              onDismiss();
            }}
            title="Dismiss notification"
            aria-label="Dismiss"
          >
            <X size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}
